import {
  Button,
  Flex,
  Modal,
  ScrollArea,
  Select,
  Textarea,
} from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useCallback } from "react";

import { CreateLimitRequestPaymasterInput } from "../../../graphql/generated.ts";
import {
  LimitRequestPaymaster,
  limitRequestPaymasterStatusOptions,
} from "../../../types/limitRequest/paymaster.ts";
import { Paymaster } from "../../../types/paymaster.ts";

interface Props {
  data: LimitRequestPaymaster | null | undefined;
  paymasters: Paymaster[];
  opened?: boolean;
  onClose: () => void;
  onSubmit: (
    value: Omit<CreateLimitRequestPaymasterInput, "limitRequestId">
  ) => void;
  onCreatePaymaster: (name: string) => undefined;
}

export const CreateLimitRequestPaymasterModal = ({
  data,
  paymasters,
  opened = false,
  onClose,
  onSubmit,
  onCreatePaymaster,
}: Props) => {
  const form = useForm({
    initialValues: {
      paymasterId: data?.paymaster.id ? String(data.paymaster.id) : "",
      status: data?.status,
      notes: data?.notes,
    },
    validate: {
      paymasterId: isNotEmpty("Paymaster Name is required"),
    },
    validateInputOnBlur: true,
    clearInputErrorOnChange: true,
  });

  const paymasterOptions = (paymasters ?? []).map((p) => ({
    label: p.name ?? "",
    value: String(p.id),
  }));

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  const handleSubmit = useCallback(() => {
    if (!form.isValid()) {
      return;
    }

    onSubmit({
      ...form.values,
      paymasterId: Number(form.values.paymasterId),
    });
    handleClose();
  }, [form, handleClose, onSubmit]);

  return (
    <>
      <Modal
        opened={opened}
        title={data?.id ? "Update Paymaster" : "Add Paymaster"}
        closeButtonProps={{
          size: 24,
          iconSize: 24,
        }}
        size="md"
        scrollAreaComponent={ScrollArea.Autosize}
        onClose={handleClose}
      >
        <Flex direction="column" gap={20}>
          <Flex direction="column" gap={12}>
            <Select
              label="Paymaster Name"
              placeholder="Select Paymaster Name"
              required
              searchable
              creatable
              disabled={!!data?.paymaster.id}
              getCreateLabel={(query) => `+ Create ${query}`}
              size="m"
              data={paymasterOptions}
              onCreate={onCreatePaymaster}
              {...form.getInputProps("paymasterId")}
            />
            <Select
              size="m"
              label="Approval Status"
              placeholder="Select Approval Status"
              data={limitRequestPaymasterStatusOptions}
              {...form.getInputProps("status")}
            />
            <Textarea
              autosize
              label="Notes"
              placeholder="Enter Notes"
              minRows={3}
              size="m"
              {...form.getInputProps("notes")}
            />
          </Flex>
          <Flex gap={20} justify="end">
            <Button
              variant="outlineBlue"
              uppercase
              type="reset"
              onClick={onClose}
            >
              cancel
            </Button>
            <Button
              variant="filledBlue"
              uppercase
              type="submit"
              disabled={!form.isValid()}
              onClick={handleSubmit}
            >
              add
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
};
